<template>
  <PreviewCardSkeleton v-if="isLoading" data-test="card-skeleton" />
  <ion-card
    v-else
    href=""
    :button="true"
    @click.prevent="$emit('card-clicked', id)"
    data-test="preview-card"
  >
    <ion-card-header>
      <ion-card-title>{{ title }}</ion-card-title>
    </ion-card-header>
    <ion-card-content>
      <div v-if="$slots.metadata" class="metadata">
        <slot name="metadata"></slot>
      </div>
      <div v-if="$slots.description" class="description">
        <slot name="description"></slot>
      </div>
    </ion-card-content>
    <AppButton
      class="button"
      size="small"
      type="tertiary"
      :label="$t(buttonText)"
      :disabled="false"
      aria-hidden="true"
      data-test="button"
      tabindex="-1"
    ></AppButton>
  </ion-card>
</template>

<script setup lang="ts">
import { defineProps } from "vue";
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
} from "@ionic/vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import PreviewCardSkeleton from "@/components/Skeletons/PreviewCardSkeleton/PreviewCardSkeleton.vue";
interface Props {
  id: string;
  title: string;
  buttonText: string;
  isLoading: boolean;
}

defineProps<Props>();
</script>

<style lang="scss" scoped>
ion-card {
  -webkit-box-shadow: none;
  align-items: flex-start;
  background-color: var(--background-white);
  border-radius: 24px;
  border: 2px solid var(--light-grey-border);
  box-shadow: none;
  display: flex;
  flex-direction: column;
  min-height: 202px;
  max-height: 318px;
  margin: var(--spacing-0);
  max-width: 584px;
  padding: var(--spacing-24);
  &::part(native) {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: stretch;
  }
  &:focus {
    border: 2px solid var(--ion-color-secondary);
  }
  &:hover {
    box-shadow: 0px 7px 8px 0px var(--shadow-color-3),
      0px 5px 22px 0px var(--shadow-color-1),
      0px 12px 17px 0px var(--shadow-color-2);
  }
  &:hover .tertiary,
  &:focus .tertiary {
    //hover over button when hovering over the card
    box-shadow: none;
    &::part(native) {
      background: var(--ion-color-tertiary-hover);
    }
  }
  &:active {
    border: 2px solid var(--light-grey-border);
    box-shadow: none;
  }
  &:active .tertiary {
    &::part(native) {
      background: var(--background-white);
    }
  }
  &.ion-activated {
    --webkit-transform: none;
    transform: none;
  }
}

ion-card-header {
  padding: var(--spacing-0);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-8);
}

ion-card-title {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: center;
  color: var(--text-primary);
  display: -webkit-box;
  font-size: 22px;
  font-weight: 500;
  line-height: 28px;
  overflow: hidden;
}

ion-card-content {
  color: var(--text-secondary);
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 20px;
  overflow: hidden;
  padding: var(--spacing-0);
  .metadata {
    color: var(--text-primary);
    line-height: 24px;
    margin-bottom: var(--spacing-0);
    overflow: hidden;
    text-overflow: ellipsis;
    :deep(div) {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);
    }
    :deep(p) {
      margin-bottom: 0;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.5px;
    }
  }
  .description {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    color: var(--text-tertiary);
    display: -webkit-box;
    height: 100px;
    line-height: 20px;
    margin-bottom: var(--spacing-16);
    margin-top: var(--spacing-8);
    overflow: hidden;
    text-overflow: ellipsis;
    :deep(p) {
      margin-bottom: 0;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      letter-spacing: 0.25px;
    }
  }
}

ion-button,
button {
  margin: var(--spacing-0);
  align-self: flex-start;
  &:hover {
    --box-shadow: none;
  }
  &:active {
    background-color: var(--background-white);
  }
}
.button:last-of-type {
  margin-top: auto;
}
</style>
